// Fatura Yönetimi (alış + satış faturaları listesi) kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'documents.invoices';

export const invoicesGuide = (t: TFunction): GuideContent => ({
  layout: 'list',
  title: t(`${G}.title`, 'Fatura Yönetimi nasıl kullanılır?'),
  intro: t(`${G}.intro`, 'Satış ve alış faturalarınızı tek listeden izler, yeni fatura keser, e-fatura gönderir ve gerekirse silersiniz. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Fatura Yönetimi'),
    stats: t(`${G}.regions.stats`, 'Bu ay kartları'),
    filters: t(`${G}.regions.filters`, 'Arama ve filtreler'),
    toolbar: t(`${G}.regions.toolbar`, 'Yeni fatura ve ⋮'),
    table: t(`${G}.regions.table`, 'Fatura listesi'),
  },
  steps: [
    {
      region: 'header',
      title: t(`${G}.s1.title`, '1. Alış ve satış faturaları aynı listede'),
      text: t(`${G}.s1.text`, 'Menüdeki "Alış Faturası" ve "Satış Faturası" bağlantıları aynı Fatura Yönetimi sayfasını açar; liste iki türü birlikte gösterir. Yalnız birini görmek için filtrelerdeki "Fatura Türü"nden Satış veya Alış seçin. Tür sütunu her satırın satış mı alış mı olduğunu gösterir.'),
    },
    {
      region: 'stats',
      title: t(`${G}.s2.title`, '2. Bu ayın özet kartlarını okuyun'),
      text: t(`${G}.s2.text`, 'Kartlar "Bu Ay Satış" ve "Bu Ay Alış" fatura sayısını ve toplam tutarını gösterir.'),
      tips: [
        t(`${G}.s2.tip1`, 'Hesap yalnız o an ekranda görünen sayfadaki faturalarla yapılır ("bu sayfa"); tüm ayın toplamı değildir.'),
        t(`${G}.s2.tip2`, 'İptal edilmiş faturalar sayılmaz; hızlı satış faturaları satışa dahildir.'),
      ],
    },
    {
      region: 'filters',
      title: t(`${G}.s3.title`, '3. Fatura arayın ve filtreleyin'),
      text: t(`${G}.s3.text`, '"Fatura Ara..." kutusuna yazarak arayın. Kutunun sağındaki filtre simgesi ek filtreleri açar: Ürün (fatura kalemlerindeki ürün adına göre), Ödeme Türü, Fatura Türü, E-Fatura Türü ve Şube.'),
      tips: [
        t(`${G}.s3.tip1`, 'Filtreler kapalıyken simgedeki sayı kaç filtrenin etkin olduğunu gösterir.'),
        t(`${G}.s3.tip2`, 'Ödeme Türü seçenekleri, faturaya bağlı satış fişlerinde gerçekten geçen değerlerden oluşur.'),
        t(`${G}.s3.tip3`, 'Şube müdürü olarak girdiyseniz Şube filtresi kendi şubenize kilitlidir.'),
      ],
    },
    {
      region: 'toolbar',
      title: t(`${G}.s4.title`, '4. Yeni fatura kesin, ⋮ menüsünü kullanın'),
      text: t(`${G}.s4.text`, '"Yeni Satış Faturası" ve "Yeni Alış Faturası" ilgili fatura formunu açar. ⋮ menüsünde Excel İndir, Raporlar (Fatura Raporları sayfası) ve Firma Bilgileri bulunur.'),
      tips: [
        t(`${G}.s4.tip1`, 'Şube müdürü satış faturası kesemez; alış faturasını yalnız yetkisi varsa oluşturur ve yalnız kendi şubesinin alış faturalarını düzenleyip silebilir.'),
      ],
    },
    {
      region: 'table',
      title: t(`${G}.s5.title`, '5. Satır işlemlerini kullanın'),
      text: t(`${G}.s5.text`, 'Satıra tıklamak faturayı görüntüler. Satırın sonundaki ⋮ menüsünde Görüntüle, Düzenle, Sil, Yazdır, İndir ve İşlem Geçmişi bulunur. E-fatura entegrasyonunuz varsa E-Fatura Gönder, Durum Sorgula, İade Faturası Oluştur ve E-Arşiv Faturayı İptal Et de burada çıkar.'),
      tips: [
        t(`${G}.s5.tip1`, 'Düzenlenemez: hızlı satıştan gelen (ödemesi alınmış) kapalı faturalar ve başarıyla gönderilmiş ya da e-fatura ile gelmiş faturalar. Gönderimi başarısız olan fatura düzeltilip yeniden gönderilebilir.'),
        t(`${G}.s5.tip2`, 'Sil, faturayla birlikte ona bağlı tüm cari, stok, kasa ve banka hareketlerini siler ve bakiyeleri günceller. Silmeden önce kaç hareketin silineceği gösterilir.'),
        t(`${G}.s5.tip3`, 'Başarıyla gönderilmiş e-faturalar silinemez; PavoPay faturaları ve iptal edilmiş e-arşiv faturaları bunun dışındadır.'),
        t(`${G}.s5.tip4`, 'E-Arşiv iptali önce belgeyi entegratörde iptal eder, ardından muhasebe hareketlerini geri almak için silme onayını açar. E-fatura (Temel / Ticari) iptal edilemez.'),
        t(`${G}.s5.tip5`, 'Gönderimi başarısız görünen faturayı yeniden göndermeden önce "Durum Sorgula" ile gerçek durumunu kontrol edin; aksi halde mükerrer belge oluşabilir.'),
      ],
    },
    {
      region: 'pagination',
      title: t(`${G}.s6.title`, '6. Sayfalar arasında gezin'),
      text: t(`${G}.s6.text`, 'Liste uzunsa alttaki sayfalama ile ilerleyin; sayfa başına 25, 50 veya 100 satır seçebilirsiniz. Başlıktaki (i) düğmesi bu rehberi her zaman yeniden açar.'),
    },
  ],
});
